import _ from 'lodash';
import moment from 'moment-timezone';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import './style.scss';
import { type Filter } from '../..';
import { RootState, useAppSelector } from 'careand-redux/store';
import { IsContinuityOfCareEnabled } from 'careand-redux/selectors';
import { ClinicStatistics, ClinicStatisticsNew, Physician } from 'generated/api';
import {
  Revenue,
  StarRatings,
  Subscriptions,
  BlockedTime,
  LeadTimeToAppointment,
  LeadTimeToAppointmentPhysicianOnly,
} from 'components/Clinic/ClinicStats';
import { PatientImage } from 'components/Common/UserImage';
import PhysicianUtils from 'services/PhysicianUtils';
import CalendarUtils from 'services/CalendarUtils';
import Icon from 'components/Utils/NewIcon';
import InlineToggle, { InlineSwitch } from '../../components/InlineToggle';
import { useStatistics } from '../../context';
import { Bar } from 'react-chartjs-2';
import Annotation from 'chartjs-plugin-annotation';
import { Chart } from 'chart.js';
import useFetchPatients from '../../../../../hooks/useFetchPatients';
import RouteUtils from '../../../../../services/RouteUtils';
import { useNavStack } from '../../../../../careand-redux/slices/ui/hooks';
import { Stacks } from '../../../../../careand-redux/slices/ui/navigation';
import DashboardBox, { ALLOW_ALL } from '../../components/DashboardBox';
import PatientsWithMostAppointments from '../../components/PatientsWithMostAppointments';

Chart.register(Annotation);

function showPhysician(physician?: Physician) {
  return physician && !physician.hide_portal && !physician.inactive;
}

const StaffSection = ({ filter }: { filter: Filter }) => {
  const { data } = useStatistics();

  const physicians = useAppSelector((state) => state.physicians.byId);

  const filteredPhysicians: Record<string, Physician> = useMemo(() => {
    if (filter.type === 'clinic') {
      return _.pickBy(
        physicians,
        (value) => value!.clinic_id === filter.id && showPhysician(value),
      );
    } else if (filter.type === 'practitioner' && filter.id) {
      return { [filter.id!]: physicians[filter.id]! };
    }
    return _.pickBy(physicians, showPhysician);
  }, [physicians, filter]);

  const clinicPhysicians = useMemo(() => {
    const clinicId =
      filter.type === 'clinic'
        ? filter.id
        : filter.type === 'practitioner'
        ? physicians[filter.id!]?.clinic_id
        : undefined;
    if (!clinicId) {
      return undefined;
    }
    return _.pickBy(physicians, (it) => it!.clinic_id === clinicId);
  }, [physicians, filter]);

  const clinicId = useMemo(() => {
    if (filter.type === 'clinic') {
      return filter.id;
    } else if (filter.type === 'practitioner') {
      return physicians![filter.id!]!.clinic_id;
    }
    return undefined;
  }, [filter, physicians]);

  return (
    <StaffPractitionerSection
      filter={filter}
      physicians={filteredPhysicians}
      clinicPhysicians={clinicPhysicians!}
      allPhysicians={physicians}
      data={data}
      clinicId={clinicId}
    />
  );
};

function StaffPractitionerSection({
  filter,
  clinicId,
  physicians,
  clinicPhysicians,
  data,
  allPhysicians,
}: {
  filter: Filter;
  clinicId?: string;
  clinicPhysicians: Partial<Record<string, Physician>>;
  physicians: RootState['physicians']['byId'];
  allPhysicians: RootState['physicians']['byId'];
  data: ClinicStatisticsNew | undefined;
}) {
  const apptRatingsPeriodHandler = useState(1);
  const showAbsoluteAppointmentsPerSub = useState(false);
  const continuityEnabled = useAppSelector(IsContinuityOfCareEnabled);

  return (
    <div className="staff-practitioner-section relative" key={JSON.stringify(filter)}>
      <div className="auto-two-cols">
        <DashboardBox
          title="Appointments Per Sub Per Year"
          info="Average number of appointments a subscriber books in a year"
          allow={ALLOW_ALL}
        >
          {data && <AppointmentsPerSub data={data} physicians={physicians} />}
        </DashboardBox>
        <DashboardBox
          title="Appointments Per Sub Per Year"
          allow={ALLOW_ALL}
          titleExtras={
            <InlineToggle
              titleOn="Appointments"
              titleOff="Subscribers"
              handler={showAbsoluteAppointmentsPerSub}
            />
          }
          info="Number of appointments a subscriber books in a year"
        >
          {data && (
            <AppointmentsPerSubHistogram
              data={data}
              physicians={physicians}
              showAbsoluteNumberOfAppointments={showAbsoluteAppointmentsPerSub[0]}
            />
          )}
        </DashboardBox>
      </div>

      <div className="auto-two-cols">
        <DashboardBox
          title="Revenue (excluding HST)"
          allow="super_admin"
          info="Practitioner revenue excluding HST"
        >
          {data && <Revenue data={data} physicians={physicians!} hideInactive showTitle={false} />}
        </DashboardBox>
        <DashboardBox
          title="Rostered Subscribers"
          info="Rostered subscribers per practitioner vs clinic average"
          allow={ALLOW_ALL}
          hidden={!continuityEnabled}
        >
          <Subscriptions
            data={data!}
            physicians={physicians}
            allPhysicians={allPhysicians}
            showClinics={[clinicId!]}
            showTitle={false}
            showLegend={true}
            showAverage
          />
        </DashboardBox>
      </div>

      <div className="auto-two-cols">
        <DashboardBox
          title="Blocked Time by Week"
          allow={ALLOW_ALL}
          info={
            <div>
              Blocked Time by week by practitioner
              <br />
              Counts only times explicitly blocked in the schedule (lunch time and shifts removed do
              not count)
            </div>
          }
        >
          {data && <BlockedTime blocked={data?.blocked} physicians={physicians} />}
        </DashboardBox>
        <DashboardBox
          title="Lead Time To Appointment"
          info={<div>How many days ahead, on average, are patients booking in a given month</div>}
          allow={ALLOW_ALL}
        >
          {data && (
            <LeadTimeToAppointment
              clinicPhysicians={clinicPhysicians}
              clinicId={clinicId}
              physicians={physicians}
              appointments={data.appointments!}
            />
          )}
          {data && (
            <LeadTimeToAppointmentPhysicianOnly
              physicians={physicians}
              clinicId={clinicId}
              appointments={data.appointments!}
            />
          )}
        </DashboardBox>
      </div>

      <div className="auto-two-cols">
        <DashboardBox
          className="star-ratings"
          title="Appointment Ratings"
          allow={ALLOW_ALL}
          info="Appointment ratings given by patients in-app"
        >
          {data && (
            <div className="star-charts">
              <StarRatings data={data} physicians={physicians} hideInactive />
            </div>
          )}
        </DashboardBox>
        <DashboardBox
          className="staff-feedback-section"
          title="Appointment Reviews"
          titleExtras={
            <InlineSwitch
              titles={['Last Year', 'This Year', 'This Month']}
              handler={apptRatingsPeriodHandler}
            />
          }
          allow={ALLOW_ALL}
          info="Appointment reviews given by patients in-app (reviews are only asked for ratings below 4 stars)"
        >
          <AppointmentFeedbackList
            data={data}
            clinicId={clinicId}
            physicians={Object.keys(physicians)}
            period={apptRatingsPeriodHandler[0]}
            showNP={Object.keys(physicians).length > 1}
          />
        </DashboardBox>
      </div>
    </div>
  );
}

function AppointmentFeedbackList({
  data,
  physicians,
  clinicId,
  period,
  showNP,
}: {
  clinicId?: string;
  data: ClinicStatisticsNew | undefined;
  physicians?: string[];
  period: number;
  showNP?: boolean;
}) {
  const physicianSet = useMemo(() => new Set(physicians), [physicians]);

  const appointments = useMemo(() => {
    return _(data?.appointments ?? [])
      .filter((it) => !clinicId || it.clinic_id === clinicId)
      .filter((it) => {
        if (!physicians?.length) return true;
        return !!it.physician_id && physicianSet.has(it.physician_id);
      })
      .filter((it) => !!it.feedback)
      .filter((it) => dateFilter(period, it.date!))
      .sortBy('date')
      .reverse()
      .value();
  }, [data, clinicId, physicians, period, physicianSet]);

  useFetchPatients(appointments);

  return (
    <div className="cards">
      {appointments.map((appt) => (
        <AppointmentFeedbackCard
          patient_id={appt.patient_id!}
          rating={appt.rating ?? -1}
          feedback={appt.feedback!}
          physician_id={appt.physician_id!}
          date={appt.date!}
          showNP={showNP}
        />
      ))}
    </div>
  );
}

function dateFilter(range: number, date: number) {
  const lastYear = 0;
  const thisYear = 1;
  const thisMonth = 2;

  if (range === thisYear) {
    return date > moment().startOf('year').unix();
  } else if (range === lastYear) {
    return (
      date > moment().add(-1, 'year').startOf('year').unix() &&
      date < moment().startOf('year').unix()
    );
  } else if (range === thisMonth) {
    return date >= moment().startOf('month').unix() && date <= moment().endOf('month').unix();
  }

  console.error('Unknown range');
  return false;
}

function AppointmentFeedbackCard(props: {
  patient_id: string;
  rating: number;
  feedback: string;
  date: number;
  physician_id: string;
  showNP?: boolean;
}) {
  const nav = useNavStack(Stacks.Master);
  const patient = useAppSelector((state) => state.patients.byId[props.patient_id]);
  const physician = useAppSelector((state) => state.physicians.byId[props.physician_id]);

  const ratingEl = _.range(5).map((idx) => {
    return (
      <Icon
        src={['fas', 'star']}
        height="14px"
        className={idx + 1 <= props.rating ? 'filled' : ''}
      />
    );
  });

  function goToPatientProfile() {
    let route = RouteUtils.getRoute(RouteUtils.routes().patient, {
      ':patientId': patient!.id,
    });
    nav.push(route);
  }

  return (
    <div className="appt-feedback-card">
      <div className="row-1">
        <div onClick={goToPatientProfile} className="patient-info">
          <PatientImage user={patient} size={30} />
          <div className="name">{patient?.first_name ?? patient?.preferred_name}</div>
        </div>
        <div className="separator" />
        <div className="rating">{ratingEl}</div>
        <div className="separator" />
        <div className="date">{props.date ? CalendarUtils.format(props.date) : ''}</div>
        <div className="separator" />
        {props.showNP && <div className="np truncate">{PhysicianUtils.getName(physician)}</div>}
      </div>
      <div className="feedback">{props.feedback}</div>
    </div>
  );
}

function AppointmentsPerSubHistogram({
  data,
  physicians,
  showAbsoluteNumberOfAppointments,
}: {
  data: ClinicStatisticsNew;
  physicians: RootState['physicians']['byId'];
  showAbsoluteNumberOfAppointments: boolean;
}) {
  const finalData = useMemo(() => {
    const appointmentsPerSub = _(data.appointments!)
      .filter(
        (it) =>
          !it.fee &&
          !!physicians[it.physician_id!] &&
          (it.date ?? 0) >= moment().add(-1, 'year').unix() &&
          it.status === 'Completed',
      )
      .groupBy('patient_id')
      .mapValues((it) => it.length)
      .values()
      .value();

    const dataset: { [x: number]: number } = {};
    const totalAppointments = _(appointmentsPerSub).sum();

    for (let number of appointmentsPerSub) {
      dataset[number] = (dataset[number] ?? 0) + 1;
    }

    const finalData = _(dataset)
      .mapValues((y, x) => {
        //@ts-expect-error
        return { x, y: showAbsoluteNumberOfAppointments ? x * y : y };
      })
      .values()
      .value();

    //@ts-expect-error
    finalData.sort((a, b) => a.x - b.x);

    const halfAppointments = totalAppointments / 2;
    let halfway = 0;
    let sumPrev = 0;
    let sum = 0;
    for (let entry of finalData) {
      halfway = entry.x as unknown as number;
      sumPrev = sum;
      sum += showAbsoluteNumberOfAppointments ? entry.y : entry.y * (entry.x as unknown as number);
      if (sum >= halfAppointments) {
        const frac = (sum - halfAppointments) / (sum - sumPrev);
        halfway = halfway - 1 + frac;
        break;
      }
    }

    return { finalData, halfway };
  }, [data, physicians, showAbsoluteNumberOfAppointments]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      colors: {
        enabled: true,
      },
      annotation: {
        annotations: {
          line1: {
            label: {
              content: '50%',
              display: true,
            },
            type: 'line',
            xMin: finalData.halfway,
            xMax: finalData.halfway,
            borderColor: 'black',
            borderWidth: 2,
          },
        },
      },
      tooltip: {
        callbacks: {
          //@ts-expect-error
          title: (items) =>
            items[0].formattedValue +
            (showAbsoluteNumberOfAppointments ? ' appointments' : ' subscribers'),
          //@ts-expect-error
          label: (item) => item.raw.x + ' appointments/year',
        },
      },
    },
    barPercentage: 1.24,
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="chart appt-per-sub-hist" style={{ height: '300px' }}>
      {/**@ts-expect-error*/}
      <Bar data={{ datasets: [{ data: finalData.finalData }] }} options={options} />
    </div>
  );
}

function AppointmentsPerSub({
  data,
  physicians,
}: {
  data: ClinicStatisticsNew;
  physicians: RootState['physicians']['byId'];
}) {
  const graphDataset = useMemo(() => {
    const dataset: { [year: number]: { [physician_id: string]: { [patientId: string]: number } } } =
      {};

    for (let appt of data.appointments ?? []) {
      if (appt.fee || !appt.physician_id || !appt.patient_id || !appt.date) {
        continue;
      }
      if (!physicians[appt.physician_id]) {
        continue;
      }
      const { physician_id, patient_id, date } = appt;
      const year = moment.unix(date!).year();

      dataset[year] = dataset[year] ?? {};
      dataset[year]![physician_id] = dataset[year]![physician_id] ?? {};
      dataset[year]![physician_id]![patient_id] =
        (dataset[year]![physician_id]![patient_id] ?? 0) + 1;
    }

    return _(dataset)
      .toPairs()
      .map(([year, value]) => {
        const values = _(value)
          .mapValues((patients, physician_id) => {
            const apptCount = _.values(patients);
            const avg = apptCount.length > 0 ? _.sum(apptCount) / apptCount.length : 0;
            return {
              x: PhysicianUtils.getName(physicians[physician_id]),
              y: avg,
            };
          })
          .values()
          .sortBy('y')
          .value();
        return {
          label: year,
          data: values,
        };
      })
      .value();
  }, [data.appointments, physicians]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
        },
        colors: {
          enabled: true,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
        },
      },
    }),
    [],
  );

  return (
    <div className="chart appt-per-sub-chart">
      <Bar data={{ datasets: graphDataset }} options={options} />
    </div>
  );
}

export default StaffSection;
